import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, Edit3, ChevronLeft } from "lucide-react";
import { api, useAuth, formatApiError } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import { toast } from "sonner";

const emptyProfile = {
  profile_type: "particulier",
  first_name: "", last_name: "",
  address_line1: "", address_line2: "", postal_code: "", city: "", country: "France",
  billing_email: "", phone: "",
  company_name: "", trade_name: "",
  siren: "", siret: "", vat_number: "", no_vat: false,
  attested: false,
};

function Field({ label, hint, required, error, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `field-${testId}` : undefined}>
      <span className="cs-eyebrow text-neutral-500">
        {label}{required ? " *" : ""}
      </span>
      <div className="mt-2">{children}</div>
      {hint && !error && <span className="mt-1 block text-[12px] font-mono text-neutral-500">{hint}</span>}
      {error && <span className="mt-1 block text-[12px] font-mono text-red-700" data-testid={testId ? `error-${testId}` : undefined}>{error}</span>}
    </label>
  );
}

function Review({ profile, onEdit, onContinue, returnLabel }) {
  const isPro = profile.profile_type === "professionnel";
  return (
    <div className="cs-card p-6" data-testid="billing-review">
      <div className="flex items-center gap-2 text-sm text-[#0E7C90] font-medium">
        <CheckCircle2 size={16} /> Informations complètes et validées
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Row k="Type" v={isPro ? "Professionnel / entreprise" : "Particulier"} />
        {isPro && <Row k="Entreprise" v={profile.company_name || "—"} />}
        {isPro && profile.trade_name && <Row k="Nom commercial" v={profile.trade_name} />}
        <Row k={isPro ? "Contact" : "Nom"} v={`${profile.first_name} ${profile.last_name}`.trim()} />
        <Row k="Adresse" v={[profile.address_line1, profile.address_line2, `${profile.postal_code} ${profile.city}`, profile.country].filter(Boolean).join(" · ")} />
        <Row k="Email de facturation" v={profile.billing_email || "—"} />
        {profile.phone && <Row k="Téléphone" v={profile.phone} />}
        {isPro && <Row k="SIREN" v={profile.siren || "—"} />}
        {isPro && <Row k="SIRET" v={profile.siret || "—"} />}
        {isPro && (
          <Row k="TVA intracom." v={profile.vat_number || (profile.no_vat ? "Non assujettie" : "—")} />
        )}
        <Row k="Dernière validation" v={profile.attested_at ? fmtDateTime(profile.attested_at) : "—"} />
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {onContinue && (
          <button onClick={onContinue} className="cs-btn-primary" data-testid="billing-continue">
            Oui, continuer{returnLabel ? ` · ${returnLabel}` : ""}
          </button>
        )}
        <button onClick={onEdit} className="cs-btn-ghost !text-black !border-black inline-flex items-center gap-2" data-testid="billing-edit">
          <Edit3 size={14} /> Modifier mes informations
        </button>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="border-b border-black/10 py-1.5">
      <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-neutral-500">{k}</div>
      <div className="mt-0.5 whitespace-pre-wrap">{v || "—"}</div>
    </div>
  );
}

export default function BillingInfo() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("return");
  const [profile, setProfile] = useState(null);
  const [meta, setMeta] = useState({ attestation_text: "", attestation_version: "" });
  const [form, setForm] = useState(emptyProfile);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/billing");
      setProfile(data.profile);
      setMeta({ attestation_text: data.attestation_text, attestation_version: data.attestation_version });
      if (!data.profile?.attested) {
        setEditing(true);
        setForm((f) => ({
          ...f,
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          billing_email: user?.email || "",
        }));
      } else {
        setForm({ ...emptyProfile, ...data.profile, attested: false });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const isPro = form.profile_type === "professionnel";

  const canSubmit = useMemo(() => {
    if (!form.attested) return false;
    const req = ["address_line1", "postal_code", "city", "country", "billing_email"];
    const need = isPro
      ? [...req, "company_name", "first_name", "last_name", "siren", "siret"]
      : [...req, "first_name", "last_name"];
    return need.every((k) => (form[k] || "").trim());
  }, [form, isPro]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const save = async () => {
    setErrors({}); setBusy(true);
    try {
      const payload = { ...form };
      if (!isPro) {
        payload.company_name = ""; payload.trade_name = "";
        payload.siren = ""; payload.siret = ""; payload.vat_number = ""; payload.no_vat = false;
      }
      const { data } = await api.put("/me/billing", payload);
      setProfile(data.profile);
      setEditing(false);
      toast.success("Informations de facturation enregistrées");
      if (returnTo) nav(returnTo);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.missing_fields) toast.error(detail.message || "Champs manquants");
      else if (typeof detail === "string") toast.error(detail);
      else if (Array.isArray(detail)) {
        const map = {};
        for (const d of detail) {
          if (d?.loc?.length >= 2) map[d.loc[1]] = d.msg;
        }
        setErrors(map);
        toast.error("Merci de corriger les champs indiqués.");
      } else toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  if (!loaded) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const canContinue = returnTo && profile?.attested;

  return (
    <div className="cs-cream py-14 min-h-[80vh]" data-testid="page-billing-info">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/espace" className="text-[13px] font-mono text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid="billing-back">
          <ChevronLeft size={14} /> Retour à mon espace
        </Link>
        <div className="cs-eyebrow text-neutral-500 mt-6">Espace client</div>
        <h1 className="cs-h1 mt-2 text-4xl">Informations de facturation</h1>
        <p className="mt-3 text-neutral-700 text-sm">
          Identité utilisée sur toutes vos futures factures Charbonier Studio. Distincte de vos informations de connexion et des détails d'une commande.
        </p>

        {returnTo && profile?.attested && !editing && (
          <div className="mt-6 cs-card p-4 text-sm bg-white" data-testid="billing-return-notice">
            Avant de poursuivre votre demande de facture, vérifiez que ces informations sont bien à jour.
          </div>
        )}

        {profile?.shared_with_fiche && (
          <div className="mt-6 cs-card p-4 text-sm bg-[#0E7C90]/5 border border-[#0E7C90]/30" data-testid="billing-shared-notice">
            <div className="flex items-center gap-2 text-[#0E7C90] font-medium">
              <CheckCircle2 size={16} /> Fiche de facturation partagée
            </div>
            <p className="mt-1 text-neutral-700">
              Votre compte est rattaché à une fiche client Charbonier Studio. Les informations affichées sont communes à {profile.fiche_linked_count || 1} compte(s) rattaché(s). Toute modification met à jour la fiche partagée sans toucher aux factures déjà émises ni à vos identifiants de connexion.
            </p>
          </div>
        )}

        <div className="mt-8">
          {profile?.attested && !editing ? (
            <Review
              profile={profile}
              onEdit={() => { setEditing(true); setForm({ ...emptyProfile, ...profile, attested: false }); }}
              onContinue={canContinue ? () => nav(returnTo) : null}
              returnLabel={canContinue ? "revenir à ma commande" : null}
            />
          ) : (
            <div className="cs-card p-6" data-testid="billing-form">
              {profile && !profile.attested && (
                <div className="flex items-center gap-2 text-[13px] font-mono text-amber-700 mb-4">
                  <AlertTriangle size={14} /> Informations à compléter
                </div>
              )}

              <Field label="Vous êtes" required testId="profile_type">
                <div className="flex gap-2">
                  {[
                    { v: "particulier", l: "Un particulier" },
                    { v: "professionnel", l: "Un professionnel / une entreprise" },
                  ].map((o) => (
                    <button key={o.v} type="button"
                      onClick={() => setField("profile_type", o.v)}
                      className={`flex-1 px-4 py-3 text-sm border ${form.profile_type === o.v ? "bg-black text-[#FAF8F5] border-black" : "bg-white border-black/25 hover:border-black"}`}
                      data-testid={`profile-type-${o.v}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </Field>

              {isPro && (
                <>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <Field label="Raison sociale" required error={errors.company_name} testId="company_name">
                      <input className="cs-input" value={form.company_name} onChange={(e) => setField("company_name", e.target.value)} data-testid="input-company_name" />
                    </Field>
                    <Field label="Nom commercial" hint="Facultatif" testId="trade_name">
                      <input className="cs-input" value={form.trade_name} onChange={(e) => setField("trade_name", e.target.value)} data-testid="input-trade_name" />
                    </Field>
                  </div>
                </>
              )}

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field label={isPro ? "Prénom du contact" : "Prénom"} required error={errors.first_name} testId="first_name">
                  <input className="cs-input" autoComplete="given-name" value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} data-testid="input-first_name" />
                </Field>
                <Field label={isPro ? "Nom du contact" : "Nom"} required error={errors.last_name} testId="last_name">
                  <input className="cs-input" autoComplete="family-name" value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} data-testid="input-last_name" />
                </Field>
              </div>

              <div className="mt-6">
                <Field label="Adresse de facturation" required error={errors.address_line1} testId="address_line1">
                  <input className="cs-input" autoComplete="address-line1" value={form.address_line1} onChange={(e) => setField("address_line1", e.target.value)} data-testid="input-address_line1" />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Complément d'adresse" hint="Facultatif" testId="address_line2">
                  <input className="cs-input" autoComplete="address-line2" value={form.address_line2} onChange={(e) => setField("address_line2", e.target.value)} data-testid="input-address_line2" />
                </Field>
              </div>
              <div className="mt-4 grid sm:grid-cols-3 gap-4">
                <Field label="Code postal" required error={errors.postal_code} testId="postal_code">
                  <input className="cs-input" inputMode="numeric" autoComplete="postal-code" value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} data-testid="input-postal_code" />
                </Field>
                <Field label="Ville" required error={errors.city} testId="city">
                  <input className="cs-input" autoComplete="address-level2" value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="input-city" />
                </Field>
                <Field label="Pays" required error={errors.country} testId="country">
                  <input className="cs-input" autoComplete="country-name" value={form.country} onChange={(e) => setField("country", e.target.value)} data-testid="input-country" />
                </Field>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <Field label="Email de facturation" required error={errors.billing_email} testId="billing_email">
                  <input type="email" className="cs-input" autoComplete="email" value={form.billing_email} onChange={(e) => setField("billing_email", e.target.value)} data-testid="input-billing_email" />
                </Field>
                <Field label="Téléphone" hint="Facultatif" testId="phone">
                  <input type="tel" className="cs-input" autoComplete="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="input-phone" />
                </Field>
              </div>

              {isPro && (
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <Field label="SIREN" required hint="Numéro à 9 chiffres identifiant l'entreprise." error={errors.siren} testId="siren">
                    <input className="cs-input" inputMode="numeric" maxLength={9} value={form.siren} onChange={(e) => setField("siren", e.target.value.replace(/\D/g, ""))} data-testid="input-siren" />
                  </Field>
                  <Field label="SIRET" required hint="Numéro à 14 chiffres identifiant l'établissement." error={errors.siret} testId="siret">
                    <input className="cs-input" inputMode="numeric" maxLength={14} value={form.siret} onChange={(e) => setField("siret", e.target.value.replace(/\D/g, ""))} data-testid="input-siret" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Numéro de TVA intracommunautaire" hint="Laissez ce champ vide si votre entreprise ne possède pas de numéro de TVA intracommunautaire." error={errors.vat_number} testId="vat_number">
                      <input className="cs-input" value={form.vat_number} onChange={(e) => setField("vat_number", e.target.value.toUpperCase())} placeholder="FR34410127518" disabled={form.no_vat} data-testid="input-vat_number" />
                    </Field>
                    <label className="mt-2 flex items-center gap-2 text-[13px]">
                      <input type="checkbox" checked={form.no_vat} onChange={(e) => setField("no_vat", e.target.checked)} data-testid="input-no_vat" />
                      Je ne possède pas de numéro de TVA intracommunautaire
                    </label>
                  </div>
                </div>
              )}

              <div className="mt-8 border-t border-black/10 pt-5">
                <label className="flex items-start gap-3 text-[13px] leading-relaxed" data-testid="attestation">
                  <input type="checkbox" className="mt-1" checked={form.attested} onChange={(e) => setField("attested", e.target.checked)} data-testid="input-attested" />
                  <span>{meta.attestation_text}</span>
                </label>
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={save}
                    disabled={!canSubmit || busy}
                    className="cs-btn-primary disabled:opacity-40"
                    data-testid="billing-save"
                  >
                    {busy && <Loader2 size={14} className="animate-spin" />}
                    Enregistrer mes informations de facturation
                  </button>
                  {profile?.attested && (
                    <button type="button" onClick={() => { setEditing(false); setErrors({}); }} className="cs-btn-ghost !text-black !border-black" data-testid="billing-cancel">
                      Annuler
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
